import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

interface AudioState {
  playing: boolean;
  muted: boolean;
  duration: number;
  currentTime: number;
  meta: any;
}

interface AudioAction {
  type: string;
  payload?: any;
}

const AudioPlayerContext = createContext<Partial<AudioState & any>>({});

const reducers = {
  SET_META(state: AudioState, action: AudioAction) {
    return { ...state, meta: action.payload };
  },
  PLAY(state: AudioState, _action: AudioAction) {
    return { ...state, playing: true };
  },
  PAUSE(state: AudioState, _action: AudioAction) {
    return { ...state, playing: false };
  },
  TOGGLE_MUTE(state: AudioState, _action: AudioAction) {
    return { ...state, muted: !state.muted };
  },
  SET_CURRENT_TIME(state: AudioState, action: AudioAction) {
    return { ...state, currentTime: action.payload };
  },
  SET_DURATION(state: AudioState, action: AudioAction) {
    return { ...state, duration: action.payload };
  },
};

function audioReducer(state: AudioState, action: AudioAction) {
  const reducer = reducers[action.type as keyof typeof reducers];
  return reducer ? reducer(state, action) : state;
}

interface AudioProviderProps {
  children: ReactNode;
  meta?: any;
  crossOrigin?: 'anonymous' | 'use-credentials' | null;
}

export function AudioProvider({
  meta,
  children,
  crossOrigin = 'anonymous',
}: AudioProviderProps) {
  const [state, dispatch] = useReducer(audioReducer, {
    playing: false,
    muted: false,
    duration: 0,
    currentTime: 0,
    meta: meta || null,
  });
  const playerRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (meta) {
      dispatch({ type: 'SET_META', payload: meta });

      if (playerRef.current?.currentSrc !== meta.audio.src) {
        const playbackRate = playerRef.current?.playbackRate;
        if (playerRef.current) {
          playerRef.current.src = meta.audio.src;
          playerRef.current.load();
          playerRef.current.pause();
          playerRef.current.playbackRate = playbackRate || 1;
          playerRef.current.currentTime = 0;
        }
      }
    }
  }, [meta]);

  const actions = useMemo(() => {
    return {
      play(data: any) {
        if (data) {
          dispatch({ type: 'SET_META', payload: data });

          if (playerRef.current?.currentSrc !== data.audio.src) {
            const playbackRate = playerRef.current?.playbackRate;
            if (playerRef.current) {
              playerRef.current.src = data.audio.src;
              playerRef.current.load();
              playerRef.current.pause();
              playerRef.current.playbackRate = playbackRate || 1;
              playerRef.current.currentTime = 0;
            }
          }
        }

        playerRef.current?.play().catch((error: Error) => {
          console.warn('Audio playback failed', error.name, error.message);
        });
      },
      pause() {
        playerRef.current?.pause();
      },
      toggle(data: any) {
        this.isPlaying(data) ? actions.pause() : actions.play(data);
      },
      seekBy(amount: number) {
        if (playerRef.current) {
          playerRef.current.currentTime += amount;
        }
      },
      seek(time: number) {
        if (playerRef.current) {
          playerRef.current.currentTime = time;
        }
      },
      playbackRate(rate: number) {
        if (playerRef.current) {
          playerRef.current.playbackRate = rate;
        }
      },
      toggleMute() {
        dispatch({ type: 'TOGGLE_MUTE' });
      },
      setMetaData(data: any) {
        dispatch({ type: 'SET_META', payload: data });
      },
      isPlaying(data: any) {
        return data
          ? state.playing && playerRef.current?.currentSrc === data.audio.src
          : state.playing;
      },
    };
  }, [state.playing]);

  const api = useMemo(() => ({ ...state, ...actions }), [state, actions]);

  return (
    <>
      <AudioPlayerContext.Provider value={api}>
        {children}
      </AudioPlayerContext.Provider>
      <audio
        id='global-audio-player'
        crossOrigin={crossOrigin ?? undefined}
        ref={playerRef}
        onPlay={() => dispatch({ type: 'PLAY' })}
        onPause={() => dispatch({ type: 'PAUSE' })}
        onTimeUpdate={(event) => {
          dispatch({
            type: 'SET_CURRENT_TIME',
            payload: event.currentTarget.currentTime,
          });
        }}
        onDurationChange={(event) => {
          dispatch({
            type: 'SET_DURATION',
            payload: Math.floor(event.currentTarget.duration),
          });
        }}
        muted={state.muted}
      />
    </>
  );
}

export function useAudioPlayer(data?: any) {
  const player = useContext(AudioPlayerContext);

  return useMemo(
    () => ({
      ...player,
      play() {
        player?.play(data);
      },
      toggle() {
        player?.toggle(data);
      },
      setMeta(data: any) {
        player?.setMetaData(data);
      },
      get playing() {
        return typeof player?.isPlaying === 'function'
          ? player.isPlaying(data)
          : false;
      },
    }),
    [player, data],
  );
}
